import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import FormFieldEditor from './FormFieldEditor';
import PackEditor from './PackEditor';
import FormLibrary from './FormLibrary';

// Simple icon components (no external dependency)
const Icon = ({ name, className = "w-4 h-4" }) => {
  const icons = {
    file: '',
    upload: '⬆️',
    trash: '🗑️',
    edit: '✏️',
    save: '💾',
    close: '✕',
    plus: '+',
    eye: '👁️',
    check: '✓',
    alert: '',
    move: '↔️',
    square: '▢',
  };
  return <span className={className}>{icons[name] || '•'}</span>;
};

/**
 * Form Template Manager
 * 
 * Allows admin to:
 * 1. Upload filled PDF forms
 * 2. Draw white boxes to redact/clear existing data
 * 3. Save as blank template
 * 4. Map fields to deal data (buyer name, VIN, etc.)
 */

const FORM_TYPES = [
  { value: 'bill_of_sale', label: 'Bill of Sale' },
  { value: 'buyers_order', label: 'Buyer\'s Order' },
  { value: 'as_is', label: 'As-Is Warranty Disclaimer' },
  { value: 'buyers_guide', label: 'FTC Buyer\'s Guide' },
  { value: 'odometer', label: 'Odometer Disclosure' },
  { value: 'power_of_attorney', label: 'Power of Attorney' },
  { value: 'title_app', label: 'Title Application' },
  { value: 'bmv_3724', label: 'BMV 3724 - Odometer' },
  { value: 'bmv_3771', label: 'BMV 3771 - POA' },
  { value: 'bmv_3772', label: 'BMV 3772 - Dealer Assignment' },
  { value: 'due_bill', label: 'Due Bill' },
  { value: 'we_owe', label: 'We Owe' },
  { value: 'out_of_state', label: 'Out of State Residence' },
  { value: 'out_of_state_inspection', label: 'Out of State Inspection' },
  { value: 'other', label: 'Other' },
];

// PDF Viewer with drawing capabilities using PDF.js for accurate coordinates
const PdfEditor = ({ templateId, pdfUrl, mode, redactionZones, setRedactionZones, fieldMappings, setFieldMappings, availableFields, onSave }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1.5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 612, height: 792 });
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // For field placement
  const [placingField, setPlacingField] = useState(null);
  const [currentFontSize, setCurrentFontSize] = useState(9); // Default 9pt for Ohio DMV forms
  const [currentBold, setCurrentBold] = useState(false);
  const [currentUppercase, setCurrentUppercase] = useState(true); // Most form fields are uppercase
  
  // For editing existing field properties
  const [editingFieldId, setEditingFieldId] = useState(null);
  
  // For dragging existing fields
  const [draggingField, setDraggingField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Load PDF using PDF.js
  useEffect(() => {
    const loadPdf = async () => {
      setLoading(true);
      try {
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
        const pdfjsWorker = await import('pdfjs-dist/legacy/build/pdf.worker.entry');
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
        
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      }
      setLoading(false);
    };
    loadPdf();
  }, [pdfUrl]);

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;
      
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      // Store actual PDF dimensions (at scale 1)
      // IMPORTANT: PDF.js viewport includes rotation, so width/height are "visual" dimensions
      const originalViewport = page.getViewport({ scale: 1 });
      const rotation = page.rotate || 0;
      console.log(`[PDF.js] Page ${currentPage}: viewport=${originalViewport.width}x${originalViewport.height}, rotation=${rotation}deg`);
      setPdfDimensions({ 
        width: originalViewport.width, 
        height: originalViewport.height,
        rotation: rotation
      });
      
      await page.render({ canvasContext: context, viewport }).promise;
    };
    renderPage();
  }, [pdfDoc, currentPage, scale]);

  // Calculate click percentage - REMOVED since we do this inline now

  // Convert stored percentage back to screen position for displaying markers
  const percentToScreen = (percentX, percentY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { 
      x: percentX * rect.width, 
      y: percentY * rect.height 
    };
  };

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Debug logging to see the dimension mismatch
    console.log('Canvas pixel size:', canvas.width, 'x', canvas.height);
    console.log('Canvas display size:', rect.width.toFixed(0), 'x', rect.height.toFixed(0));
    console.log('Click position:', clickX.toFixed(0), ',', clickY.toFixed(0));
    
    // Calculate percentage based on DISPLAYED size (what user sees)
    const percentX = clickX / rect.width;   // 0 = left edge, 1 = right edge
    let percentY = clickY / rect.height;  // 0 = top edge, 1 = bottom edge
    
    console.log('Calculated percent:', (percentX*100).toFixed(1), '%,', (percentY*100).toFixed(1), '%');
    console.log(`[EDITOR CLICK] at ${(percentX*100).toFixed(1)}% x ${(percentY*100).toFixed(1)}% of visible canvas`);

    if (mode === 'redact') {
      setIsDrawing(true);
      setDrawStart({ percentX, percentY, clickX, clickY });
    } else if (mode === 'fields' && placingField) {
      setFieldMappings(prev => {
        // SNAP TO GRID: If placing within 1% of existing field, snap to same Y position
        console.log(`[SNAP CHECK] Checking ${prev.length} existing fields for snap to ${percentY.toFixed(4)}`);
        
        let targetY = percentY;
        const existingField = prev.find(f => {
          const diff = Math.abs(f.percentY - percentY);
          console.log(`[SNAP CHECK] ${f.field} at ${f.percentY.toFixed(4)}, diff: ${diff.toFixed(4)}`);
          return diff < 0.01;
        });
        
        if (existingField) {
          console.log(`[SNAP] Snapping ${placingField.key} from ${percentY.toFixed(4)} to same Y as ${existingField.field}: ${existingField.percentY.toFixed(4)}`);
          targetY = existingField.percentY;
        } else {
          console.log(`[SNAP] No snap target found for ${placingField.key} at ${percentY.toFixed(4)}`);
        }

        const newMapping = {
          id: Date.now() + Math.random(),
          page: currentPage,
          percentX,
          percentY: targetY,
          field: placingField.key,
          label: placingField.label,
          fontSize: currentFontSize,
          bold: currentBold,
          uppercase: currentUppercase,
          format: placingField.format || null,
        };
        console.log(`[EDITOR PLACED] "${placingField.key}" at ${(percentX*100).toFixed(1)}% x ${(targetY*100).toFixed(1)}% of visible canvas`);
        console.log('[PLACE]', placingField.key, 'percentY:', targetY.toFixed(4));
        
        return [...prev, newMapping];
      });

      setPlacingField(null);
      setDraggingField(null);
    }
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Calculate percentage for current mouse position
    const percentX = clickX / rect.width;
    const percentY = clickY / rect.height;

    if (isDrawing && drawStart) {
      setCurrentRect({
        percentX: Math.min(drawStart.percentX, percentX),
        percentY: Math.min(drawStart.percentY, percentY),
        percentWidth: Math.abs(percentX - drawStart.percentX),
        percentHeight: Math.abs(percentY - drawStart.percentY),
        // Screen coords for display
        screenX: Math.min(drawStart.clickX, clickX),
        screenY: Math.min(drawStart.clickY, clickY),
        screenWidth: Math.abs(clickX - drawStart.clickX),
        screenHeight: Math.abs(clickY - drawStart.clickY),
      });
    }
    
    // Update dragged field position - use functional update to prevent corruption
    if (draggingField) {
      setFieldMappings(prev => {
        const field = prev.find(f => f.id === draggingField);
        if (!field) return prev;
        
        const newPercentX = (clickX - dragOffset.x) / rect.width;
        const newPercentY = (clickY - dragOffset.y) / rect.height;
        
        // Log every movement to catch corruption in real-time
        if (Math.abs(field.percentY - newPercentY) > 0.0001) {
          console.log(`[MOVE] ${field.field} from ${field.percentY.toFixed(4)} to ${newPercentY.toFixed(4)} (offsetY: ${dragOffset.y.toFixed(1)})`);
        }
        
        return prev.map(f => 
          f.id === draggingField ? { ...f, percentX: newPercentX, percentY: newPercentY } : f
        );
      });
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentRect && currentRect.screenWidth > 5 && currentRect.screenHeight > 5) {
      // Save as percentage-based zone
      const zone = {
        id: Date.now(),
        page: currentPage,
        percentX: currentRect.percentX,
        percentY: currentRect.percentY,
        percentWidth: currentRect.percentWidth,
        percentHeight: currentRect.percentHeight,
      };
      
      console.log('Saving redaction zone:', zone);
      setRedactionZones([...redactionZones, zone]);
    }
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
    setDraggingField(null);
  };
  
  const startDragField = (e, field) => {
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate offset from marker top-left
    const markerX = field.percentX * rect.width;
    const markerY = field.percentY * rect.height;
    
    setDragOffset({
      x: mouseX - markerX,
      y: mouseY - markerY
    });
    setDraggingField(field.id);
    console.log('Started dragging field:', field.field);
  };

  const removeRedaction = (id) => {
    setRedactionZones(redactionZones.filter(z => z.id !== id));
  };

  const removeFieldMapping = (id) => {
    setFieldMappings(prev => prev.filter(f => f.id !== id));
  };

  // Get screen position for a redaction zone (percentage-based)
  const getZoneScreenPos = (zone) => {
    if (!canvasRef.current) return { left: 0, top: 0, width: 0, height: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { 
      left: zone.percentX * rect.width, 
      top: zone.percentY * rect.height, 
      width: zone.percentWidth * rect.width, 
      height: zone.percentHeight * rect.height 
    };
  };

  // Get screen position for a field (percentage-based)
  const getFieldScreenPos = (field) => {
    if (!canvasRef.current) return { left: 0, top: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { 
      left: field.percentX * rect.width, 
      top: field.percentY * rect.height 
    };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-gray-500">Loading PDF...</div></div>;
  }

  if (!pdfDoc) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-2">
        <div className="text-red-500 text-sm font-medium">PDF file not available</div>
        <div className="text-gray-500 text-xs max-w-sm">
          The PDF for this form couldn't be loaded — usually because the file lives on the
          production server and isn't present in this environment. Saved redactions and field
          mappings are untouched; printing works wherever the file exists.
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-full">
      {/* PDF Display */}
      <div className="flex-1 bg-gray-100 rounded-lg overflow-auto relative">
        <div className="sticky top-0 z-10 bg-white border-b p-2 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))} className="px-2 py-1 bg-gray-200 rounded">-</button>
            <span className="text-sm">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(3, s + 0.25))} className="px-2 py-1 bg-gray-200 rounded">+</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50">←</button>
            <span className="text-sm">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50">→</button>
          </div>
          <div className="ml-auto text-sm text-gray-500">
            {mode === 'redact' ? 'Click and drag to draw white boxes over data to clear' : 'Click a field below, then click on the PDF to place it'}
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative inline-block m-4"
          style={{ 
            cursor: mode === 'redact' ? 'crosshair' : (placingField ? 'crosshair' : 'default'),
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* PDF rendered to canvas */}
          <canvas
            ref={canvasRef}
            style={{ border: '1px solid #ccc', display: 'block' }}
          />

          {/* Redaction zones overlay */}
          {redactionZones.filter(z => z.page === currentPage).map(zone => {
            const pos = getZoneScreenPos(zone);
            return (
              <div
                key={zone.id}
                className="absolute bg-white border-2 border-red-400 group"
                style={{
                  left: pos.left,
                  top: pos.top,
                  width: pos.width,
                  height: pos.height,
                }}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); removeRedaction(zone.id); }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            );
          })}

          {/* Field mappings overlay */}
          {fieldMappings.filter(f => f.page === currentPage).map(field => {
            const pos = getFieldScreenPos(field);
            return (
              <div
                key={field.id}
                className={`absolute bg-blue-100 border-2 px-1 text-xs group cursor-move select-none ${draggingField === field.id ? 'border-green-500 bg-green-100' : 'border-blue-500'}`}
                style={{
                  left: pos.left,
                  top: pos.top,
                  height: field.fontSize * scale, // Match height to font size
                  fontSize: field.fontSize * scale * 0.8,
                  zIndex: draggingField === field.id ? 100 : 10,
                  pointerEvents: placingField ? 'none' : 'auto', // Disable clicks while placing new field
                  display: 'flex',
                  alignItems: 'flex-start', // Anchor text to the top
                  lineHeight: 1
                }}
                onMouseDown={(e) => startDragField(e, field)}
              >
                <span className={draggingField === field.id ? 'text-green-700' : 'text-blue-700'}>{field.label}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFieldMapping(field.id); }}
                  className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            );
          })}

          {/* Current drawing rectangle */}
          {currentRect && (
            <div
              className="absolute bg-white/80 border-2 border-dashed border-red-500"
              style={{
                left: currentRect.screenX,
                top: currentRect.screenY,
                width: currentRect.screenWidth,
                height: currentRect.screenHeight,
              }}
            />
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-72 bg-white border rounded-lg p-4 overflow-y-auto">
        {mode === 'redact' ? (
          <>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Icon name="square" />
              Redaction Zones
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Draw rectangles over any existing customer data to white it out. This creates your blank template.
            </p>
            <div className="space-y-2 mb-4">
              {redactionZones.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No zones drawn yet</p>
              ) : (
                redactionZones.map((zone, i) => (
                  <div key={zone.id} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                    <span>Zone {i + 1} (Page {zone.page})</span>
                    <button onClick={() => removeRedaction(zone.id)} className="text-red-500 hover:text-red-700">
                      <Icon name="trash" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <button
              onClick={onSave}
              disabled={redactionZones.length === 0}
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Icon name="save" />
              Save Blank Template
            </button>
          </>
        ) : (
          <>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Icon name="move" />
              Field Placement
            </h3>
            
            {/* Font Size & Style Controls */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Field Style</div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm">Size:</label>
                <select 
                  value={currentFontSize} 
                  onChange={(e) => setCurrentFontSize(Number(e.target.value))}
                  className="px-2 py-1 border rounded text-sm"
                >
                  {[6, 7, 8, 9, 10, 11, 12, 14].map(size => (
                    <option key={size} value={size}>{size}pt</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1 text-sm">
                  <input 
                    type="checkbox" 
                    checked={currentBold} 
                    onChange={(e) => setCurrentBold(e.target.checked)}
                    className="rounded"
                  />
                  Bold
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input 
                    type="checkbox" 
                    checked={currentUppercase} 
                    onChange={(e) => setCurrentUppercase(e.target.checked)}
                    className="rounded"
                  />
                  UPPERCASE
                </label>
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-3">
              Select a field, then click on the PDF to place it.
            </p>
            
            {placingField && (
              <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                Placing: <strong>{placingField.label}</strong> ({currentFontSize}pt{currentBold ? ', Bold' : ''}{currentUppercase ? ', CAPS' : ''})
                <button onClick={() => setPlacingField(null)} className="ml-2 text-blue-600 hover:underline">Cancel</button>
              </div>
            )}

            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {availableFields.map(group => (
                <div key={group.group}>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{group.group}</div>
                  <div className="space-y-1">
                    {group.fields.map(field => (
                      <button
                        key={field.key}
                        onClick={() => setPlacingField(field)}
                        className={`w-full text-left text-sm px-2 py-1 rounded ${placingField?.key === field.key ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                      >
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Placed Fields ({fieldMappings.length})</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {fieldMappings.map(f => (
                  <div key={f.id} className="text-xs bg-gray-50 p-2 rounded border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{f.label}</span>
                      <button onClick={() => removeFieldMapping(f.id)} className="text-red-500 hover:text-red-700">×</button>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <span>p{f.page}</span>
                      <select 
                        value={f.fontSize || 9} 
                        onChange={(e) => {
                          const newSize = Number(e.target.value);
                          setFieldMappings(prev => prev.map(fm => 
                            fm.id === f.id ? { ...fm, fontSize: newSize } : fm
                          ));
                        }}
                        className="px-1 py-0.5 border rounded text-xs"
                      >
                        {[6, 7, 8, 9, 10, 11, 12, 14].map(size => (
                          <option key={size} value={size}>{size}pt</option>
                        ))}
                      </select>
                      <label className="flex items-center gap-0.5">
                        <input 
                          type="checkbox" 
                          checked={f.bold || false} 
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldMappings(prev => prev.map(fm => 
                              fm.id === f.id ? { ...fm, bold: checked } : fm
                            ));
                          }}
                          className="w-3 h-3"
                        />
                        <span>B</span>
                      </label>
                      <label className="flex items-center gap-0.5">
                        <input 
                          type="checkbox" 
                          checked={f.uppercase !== false} 
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldMappings(prev => prev.map(fm => 
                              fm.id === f.id ? { ...fm, uppercase: checked } : fm
                            ));
                          }}
                          className="w-3 h-3"
                        />
                        <span>AA</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={onSave}
              className="w-full mt-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <Icon name="save" />
              Save Field Mappings
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Main Component
const FormTemplateManager = ({ onClose, titlesOnly = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showSplitUpload, setShowSplitUpload] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editorMode, setEditorMode] = useState('redact'); // 'redact' or 'fields'
  const [availableFields, setAvailableFields] = useState([]);
  const [fieldEditorTemplate, setFieldEditorTemplate] = useState(null); // For new visual field editor

  // Form Packs state
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [showCreatePack, setShowCreatePack] = useState(false);
  const [showPackEditor, setShowPackEditor] = useState(false);
  const [showFormLibrary, setShowFormLibrary] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackDescription, setNewPackDescription] = useState('');
  const [editingPack, setEditingPack] = useState(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('bill_of_sale');
  const [uploadRetail, setUploadRetail] = useState(true);
  const [uploadWholesale, setUploadWholesale] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPackId, setUploadPackId] = useState(null); // Which pack to upload to
  const [uploadPackIds, setUploadPackIds] = useState([]); // Multi-pack for shared templates (titlesOnly)
  // Multi-file queue: [{file, name, type, status: 'pending'|'uploading'|'done'|'error'}]
  const [uploadQueue, setUploadQueue] = useState([]);

  // Blank form creation state (titlesOnly)
  const [showBlankForm, setShowBlankForm] = useState(false);
  const [blankFormName, setBlankFormName] = useState('');
  const [blankFormPackId, setBlankFormPackId] = useState(null);
  const [creatingBlank, setCreatingBlank] = useState(false);

  // Link-from-pack state (titlesOnly)
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState([]); // [{pack, templates:[]}]
  const [selectedLinkIds, setSelectedLinkIds] = useState(new Set());
  const [linking, setLinking] = useState(false);

  // Split PDF state
  const [packFilename, setPackFilename] = useState(null);
  const [packFileUrl, setPackFileUrl] = useState(null);
  const [packPageCount, setPackPageCount] = useState(0);
  const [splits, setSplits] = useState([]);

  // Editor state
  const [redactionZones, setRedactionZones] = useState([]);
  const [fieldMappings, setFieldMappings] = useState([]);

  useEffect(() => {
    loadAvailableFields();
    loadPacks();
  }, [titlesOnly]);

  useEffect(() => {
    loadTemplates();
  }, [titlesOnly, selectedPackId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      if (titlesOnly && selectedPackId) {
        const data = await api.getPackTemplates(selectedPackId);
        setTemplates(data);
      } else {
        const data = await api.getFormTemplates(titlesOnly ? 'titles' : undefined);
        // Client-side safety: when titlesOnly, only show templates that belong to a titles pack
        if (titlesOnly) {
          const titlePackIds = new Set(packs.map(p => p.id));
          setTemplates(titlePackIds.size > 0 ? data.filter(t => titlePackIds.has(t.form_pack_id)) : data);
        } else {
          setTemplates(data);
        }
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableFields = async () => {
    try {
      const data = await api.getAvailableFields();
      setAvailableFields(data);
    } catch (err) {
      console.error('Failed to load fields:', err);
    }
  };

  const loadPacks = async () => {
    try {
      const data = await api.getFormPacks(titlesOnly ? 'titles' : undefined);
      // Client-side safety: when titlesOnly, hard-filter to only titles packs regardless of API response
      const filtered = titlesOnly ? data.filter(p => p.pack_type === 'titles') : data;
      setPacks(filtered);
      // NOTE: no pack auto-select anymore — the sidebar is gone and the template list
      // always shows everything; packs are managed in the Pack Editor.
      if (filtered.length > 0 && !uploadPackId) {
        setUploadPackId(filtered.find(p => p.is_active)?.id || filtered[0].id);
      }
    } catch (err) {
      console.error('Failed to load packs:', err);
    }
  };

  // Load link candidates: all templates from OTHER title packs
  const openLinkModal = async () => {
    if (!selectedPackId) return;
    try {
      const otherPacks = packs.filter(p => p.id !== selectedPackId);
      const candidates = await Promise.all(
        otherPacks.map(async (pack) => {
          const templates = await api.getPackTemplates(pack.id);
          return { pack, templates };
        })
      );
      setLinkCandidates(candidates.filter(c => c.templates.length > 0));
      setSelectedLinkIds(new Set());
      setShowLinkModal(true);
    } catch (err) {
      alert('Failed to load templates: ' + err.message);
    }
  };

  const handleLink = async () => {
    const ids = Array.from(selectedLinkIds);
    if (ids.length === 0) return;
    setLinking(true);
    try {
      await api.linkTemplatesToPack(selectedPackId, ids);
      setShowLinkModal(false);
      setSelectedLinkIds(new Set());
      await loadTemplates();
    } catch (err) {
      alert('Failed to link templates: ' + err.message);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (templateId) => {
    if (!selectedPackId) return;
    if (!window.confirm('Remove this linked form from this pack? It will still exist in its original pack.')) return;
    try {
      await api.unlinkTemplateFromPack(selectedPackId, templateId);
      await loadTemplates();
    } catch (err) {
      alert('Failed to unlink: ' + err.message);
    }
  };

  const handleCreatePack = async () => {
    if (!newPackName.trim()) {
      alert('Please enter a pack name');
      return;
    }
    try {
      const pack = await api.createFormPack(newPackName, newPackDescription, titlesOnly ? 'titles' : 'retail');
      setPacks([...packs, pack]);
      setShowCreatePack(false);
      setNewPackName('');
      setNewPackDescription('');
    } catch (err) {
      alert('Failed to create pack: ' + err.message);
    }
  };

  const handleActivatePack = async (packId) => {
    try {
      await api.activateFormPack(packId);
      loadPacks();
    } catch (err) {
      alert('Failed to activate pack: ' + err.message);
    }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('Delete this form pack? Templates must be moved or deleted first.')) return;
    try {
      await api.deleteFormPack(packId);
      setPacks(packs.filter(p => p.id !== packId));
      if (selectedPackId === packId) {
        setSelectedPackId(null);
      }
    } catch (err) {
      alert('Failed to delete pack: ' + err.message);
    }
  };

  const handleMoveTemplate = async (templateId, newPackId) => {
    try {
      await api.moveTemplateTopack(templateId, newPackId);
      loadTemplates();
    } catch (err) {
      alert('Failed to move template: ' + err.message);
    }
  };

  const handleAddToOutOfStateOnly = async (templateId) => {
    try {
      const response = await fetch(`/api/forms/templates/${templateId}/add-to-out-of-state`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add to Out of State pack');
      }
      
      const result = await response.json();
      alert(result.message);
    } catch (err) {
      alert('Failed to add to Out of State pack: ' + err.message);
    }
  };

  // Filter templates by selected pack (titlesOnly: already loaded per-pack or all; retail: filter by form_pack_id)
  const filteredTemplates = titlesOnly 
    ? templates 
    : (selectedPackId ? templates.filter(t => t.form_pack_id === selectedPackId) : templates);

  // Single-file upload helper (used by both single and queue flows)
  const uploadOneFile = async (file, name, type, retail, wholesale, packId, extraPackIds) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('form_type', type);
    formData.append('use_for_retail', retail);
    formData.append('use_for_wholesale', wholesale);
    if (packId) formData.append('form_pack_id', packId);
    if (titlesOnly && extraPackIds && extraPackIds.length > 0) {
      formData.append('pack_ids', JSON.stringify(extraPackIds));
    }
    return api.uploadFormTemplate(formData);
  };

  const handleUpload = async () => {
    // Multi-file queue mode
    if (uploadQueue.length > 0) {
      if (titlesOnly && !uploadPackId) {
        alert('Please select a form pack');
        return;
      }
      const pending = uploadQueue.filter(q => q.status === 'pending');
      if (pending.some(q => !q.name.trim())) {
        alert('Please enter a name for each form');
        return;
      }
      setUploading(true);
      let lastResult = null;
      const updatedQueue = [...uploadQueue];
      for (let i = 0; i < updatedQueue.length; i++) {
        if (updatedQueue[i].status !== 'pending') continue;
        updatedQueue[i] = { ...updatedQueue[i], status: 'uploading' };
        setUploadQueue([...updatedQueue]);
        try {
          const result = await uploadOneFile(
            updatedQueue[i].file,
            updatedQueue[i].name,
            updatedQueue[i].type,
            uploadRetail,
            uploadWholesale,
            uploadPackId,
            uploadPackIds
          );
          updatedQueue[i] = { ...updatedQueue[i], status: 'done' };
          lastResult = result;
        } catch (err) {
          updatedQueue[i] = { ...updatedQueue[i], status: 'error', error: err.message };
        }
        setUploadQueue([...updatedQueue]);
      }
      await loadTemplates();
      setUploading(false);
      const errors = updatedQueue.filter(q => q.status === 'error');
      if (errors.length === 0) {
        setShowUpload(false);
        setUploadQueue([]);
        setUploadPackIds([]);
        if (lastResult) {
          setFieldEditorTemplate(lastResult);
        }
      } else {
        alert(`${errors.length} file(s) failed to upload. Check the list for details.`);
      }
      return;
    }

    // Legacy single-file mode
    if (!uploadFile || !uploadName) {
      alert('Please select a file and enter a name');
      return;
    }
    if (titlesOnly && !uploadPackId) {
      alert('Please select a form pack');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadOneFile(uploadFile, uploadName, uploadType, uploadRetail, uploadWholesale, uploadPackId, uploadPackIds);
      loadTemplates();
      setShowUpload(false);
      setUploadFile(null);
      setUploadName('');
      setUploadPackIds([]);
      if (result.message) alert(result.message);
      setEditingTemplate(result);
      setEditorMode('redact');
      setRedactionZones([]);
      setFieldMappings([]);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateBlankForm = async () => {
    if (!blankFormName.trim()) {
      alert('Please enter a name for the blank form');
      return;
    }
    const packId = blankFormPackId || selectedPackId || (packs[0]?.id);
    if (!packId) {
      alert('Please select a form pack');
      return;
    }
    setCreatingBlank(true);
    try {
      const result = await api.createBlankFormTemplate(blankFormName.trim(), packId);
      setShowBlankForm(false);
      setBlankFormName('');
      setBlankFormPackId(null);
      await loadTemplates();
      // Immediately open the field editor so the user can start placing fields
      setFieldEditorTemplate(result);
    } catch (err) {
      alert('Failed to create blank form: ' + err.message);
    } finally {
      setCreatingBlank(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setRedactionZones(template.redaction_zones || []);
    setFieldMappings(template.field_mappings || []);
    setEditorMode(template.blank_file_path ? 'fields' : 'redact');
  };

  const handleSaveRedactions = async () => {
    try {
      const result = await api.saveRedactionZones(editingTemplate.id, redactionZones);
      setTemplates(templates.map(t => t.id === result.id ? result : t));
      setEditingTemplate(result);
      setEditorMode('fields');
      alert('Blank template created! Now map the fields.');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleSaveFieldMappings = async () => {
    try {
      console.log('[SAVE BUTTON] About to save fieldMappings:', JSON.stringify(fieldMappings, null, 2));
      fieldMappings.forEach(f => {
        console.log(`[SAVE BUTTON] ${f.field}: percentY=${f.percentY?.toFixed(4)}`);
      });
      const result = await api.saveFieldMappings(editingTemplate.id, fieldMappings);
      setTemplates(templates.map(t => t.id === result.id ? result : t));
      setEditingTemplate(null);
      alert('Field mappings saved! This template is ready to use.');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.deleteFormTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      const result = await api.updateFormTemplate(template.id, { is_active: !template.is_active });
      setTemplates(templates.map(t => t.id === result.id ? result : t));
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  // NOTE: the "Delete All" button was removed from the UI on purpose — one misclick
  // wiped every template and pack. The server endpoint still exists untouched.

  // Skip redaction - use original as blank template (for already-blank forms)
  const handleSkipRedaction = async () => {
    try {
      const result = await api.saveRedactionZones(editingTemplate.id, []); // Empty redaction = copy original as blank
      setTemplates(templates.map(t => t.id === result.id ? result : t));
      setEditingTemplate(result);
      setEditorMode('fields');
      alert('Skipped redaction. Now map the fields.');
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  // Reset template - clear blank file and redactions to start over
  const handleResetTemplate = async () => {
    if (!window.confirm('This will clear all redactions and field mappings. Start over?')) return;
    try {
      const result = await api.resetFormTemplate(editingTemplate.id);
      setTemplates(templates.map(t => t.id === result.id ? result : t));
      setEditingTemplate(result);
      setRedactionZones([]);
      setFieldMappings([]);
      setEditorMode('redact');
      alert('Template reset. You can now re-do the redaction.');
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  // Split PDF handlers
  const handleUploadPack = async (file) => {
    setUploading(true);
    try {
      const result = await api.uploadFormPack(file);
      setPackFilename(result.filename);
      setPackFileUrl(result.fileUrl);
      setPackPageCount(result.pageCount);
      setSplits([{ name: '', form_type: 'bill_of_sale', startPage: 1, endPage: 1, use_for_retail: true, use_for_wholesale: false }]);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const addSplit = () => {
    const lastSplit = splits[splits.length - 1];
    const nextStart = lastSplit ? lastSplit.endPage + 1 : 1;
    setSplits([...splits, { 
      name: '', 
      form_type: 'bill_of_sale', 
      startPage: Math.min(nextStart, packPageCount), 
      endPage: Math.min(nextStart, packPageCount), 
      use_for_retail: true, 
      use_for_wholesale: false 
    }]);
  };

  const updateSplit = (index, field, value) => {
    setSplits(splits.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const removeSplit = (index) => {
    setSplits(splits.filter((_, i) => i !== index));
  };

  const handleSplitPdf = async () => {
    const validSplits = splits.filter(s => s.name && s.startPage && s.endPage);
    if (validSplits.length === 0) {
      alert('Please define at least one form with a name');
      return;
    }

    setUploading(true);
    try {
      const result = await api.splitFormPack(packFilename, validSplits, packFileUrl);
      setTemplates([...templates, ...result.templates]);
      setShowSplitUpload(false);
      setPackFilename(null);
      setPackFileUrl(null);
      setPackPageCount(0);
      setSplits([]);
      alert(`Created ${result.templates.length} form templates!`);
    } catch (err) {
      alert('Split failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // NOTE: the old two-step "Redact then Map Fields" editor (PdfEditor) was retired from
  // the UI — everything edits through the single click-and-drag Field Editor now (which
  // imports old-editor mappings on open and has a built-in White-Out mode). The legacy
  // component code is kept below, unreferenced.

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">{titlesOnly ? 'Title Form Templates' : 'Form Templates'}</h2>
            <p className="text-sm text-gray-500">{titlesOnly ? 'Out of state & repo title forms' : 'Upload and configure forms for auto-fill'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPackEditor(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              title="Compose packs: which forms print together, and in what order"
            >
              Pack Editor
            </button>
            <button
              onClick={() => setShowFormLibrary(true)}
              className="px-4 py-2 bg-neutral-700 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-2"
              title="Every form in the system — find duplicates, archive/restore, rename"
            >
              Form Library
            </button>
            {!titlesOnly && (
              <button
                onClick={() => setShowSplitUpload(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                Split Multi-Page PDF
              </button>
            )}
            <button
              onClick={() => setShowUpload(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Icon name="upload" />
              Upload Form
            </button>
            {titlesOnly && (
              <button
                onClick={() => { setBlankFormPackId(selectedPackId || packs[0]?.id || null); setShowBlankForm(true); }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                + Blank Form
              </button>
            )}
            {/* NOTE: "Link From Pack" moved into the Pack Editor — one place composes packs */}
            {onClose && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <span className="text-xl">✕</span>
              </button>
            )}
          </div>
        </div>

        {/* Split PDF Modal */}
        {showSplitUpload && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h3 className="font-semibold text-lg mb-4">Split Multi-Page PDF into Forms</h3>
              
              {!packFilename ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Upload a scanned PDF containing multiple forms. You'll then define which pages belong to each form.
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => e.target.files[0] && handleUploadPack(e.target.files[0])}
                      className="hidden"
                      id="pack-upload"
                    />
                    <label htmlFor="pack-upload" className="cursor-pointer">
                      <div className="text-4xl mb-2"></div>
                      <div className="text-sm text-gray-600">Click to select PDF file</div>
                      {uploading && <div className="mt-2 text-blue-600">Uploading...</div>}
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="font-medium">PDF uploaded: {packPageCount} pages</div>
                    <div className="text-sm text-blue-700">Define which pages belong to each form below</div>
                  </div>

                  <div className="space-y-3">
                    {splits.map((split, index) => (
                      <div key={index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-sm">Form {index + 1}</span>
                          {splits.length > 1 && (
                            <button onClick={() => removeSplit(index)} className="text-red-500 text-xs hover:underline ml-auto">Remove</button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Form Name</label>
                            <input
                              type="text"
                              value={split.name}
                              onChange={(e) => updateSplit(index, 'name', e.target.value)}
                              placeholder="e.g., Bill of Sale"
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Form Type</label>
                            <select
                              value={split.form_type}
                              onChange={(e) => updateSplit(index, 'form_type', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            >
                              {FORM_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Start Page</label>
                            <input
                              type="number"
                              min="1"
                              max={packPageCount}
                              value={split.startPage}
                              onChange={(e) => updateSplit(index, 'startPage', parseInt(e.target.value) || 1)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">End Page</label>
                            <input
                              type="number"
                              min={split.startPage}
                              max={packPageCount}
                              value={split.endPage}
                              onChange={(e) => updateSplit(index, 'endPage', parseInt(e.target.value) || split.startPage)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={split.use_for_retail}
                              onChange={(e) => updateSplit(index, 'use_for_retail', e.target.checked)}
                            />
                            Retail
                          </label>
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={split.use_for_wholesale}
                              onChange={(e) => updateSplit(index, 'use_for_wholesale', e.target.checked)}
                            />
                            Wholesale
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addSplit}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600"
                  >
                    + Add Another Form
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => { setShowSplitUpload(false); setPackFilename(null); setPackFileUrl(null); setPackPageCount(0); setSplits([]); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                {packFilename && (
                  <button
                    onClick={handleSplitPdf}
                    disabled={uploading || splits.filter(s => s.name).length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploading ? 'Processing...' : `Create ${splits.filter(s => s.name).length} Templates`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {showUpload && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h3 className="font-semibold text-lg mb-1">Upload Form Template(s)</h3>
              <p className="text-xs text-gray-500 mb-4">You can select multiple PDFs at once — give each one a name below.</p>
              
              <div className="space-y-4">
                {/* File picker — multiple allowed */}
                <div>
                  <label className="block text-sm font-medium mb-1">PDF File(s)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length === 0) return;
                      if (files.length === 1) {
                        // Single file: use legacy single-file state
                        setUploadFile(files[0]);
                        setUploadName(files[0].name.replace(/\.pdf$/i, ''));
                        setUploadQueue([]);
                      } else {
                        // Multi-file: build a queue
                        setUploadFile(null);
                        setUploadName('');
                        setUploadQueue(files.map(f => ({
                          file: f,
                          name: f.name.replace(/\.pdf$/i, ''),
                          type: 'other',
                          status: 'pending',
                        })));
                      }
                    }}
                    className="w-full border rounded-lg p-2"
                  />
                </div>

                {/* Multi-file queue — show each file with its own name/type inputs */}
                {uploadQueue.length > 1 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 border-b">
                      {uploadQueue.length} files selected — give each a name:
                    </div>
                    {uploadQueue.map((item, idx) => (
                      <div key={idx} className={`px-3 py-2 border-b last:border-0 ${item.status === 'done' ? 'bg-green-50' : item.status === 'error' ? 'bg-red-50' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400 truncate flex-1">{item.file.name}</span>
                          {item.status === 'done' && <span className="text-xs text-green-600 font-medium">✓ Done</span>}
                          {item.status === 'uploading' && <span className="text-xs text-blue-600">Uploading...</span>}
                          {item.status === 'error' && <span className="text-xs text-red-600">✗ {item.error}</span>}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => setUploadQueue(q => q.map((qi, i) => i === idx ? { ...qi, name: e.target.value } : qi))}
                            placeholder="Form name"
                            className="flex-1 border rounded px-2 py-1 text-sm"
                            disabled={item.status !== 'pending'}
                          />
                          <select
                            value={item.type}
                            onChange={(e) => setUploadQueue(q => q.map((qi, i) => i === idx ? { ...qi, type: e.target.value } : qi))}
                            className="border rounded px-2 py-1 text-sm"
                            disabled={item.status !== 'pending'}
                          >
                            {FORM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Single-file name/type inputs (only show when NOT in queue mode) */}
                {uploadQueue.length <= 1 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Form Name</label>
                      <input
                        type="text"
                        value={uploadName}
                        onChange={(e) => setUploadName(e.target.value)}
                        placeholder="e.g., Out of State Inspection"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Form Type</label>
                      <select
                        value={uploadType}
                        onChange={(e) => setUploadType(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        {FORM_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Pack selection (titlesOnly) */}
                {titlesOnly && packs.length > 0 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Form Pack (required)</label>
                      <select
                        value={uploadPackId || ''}
                        onChange={(e) => setUploadPackId(parseInt(e.target.value, 10) || null)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="">Select pack...</option>
                        {packs.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    {packs.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Also add to (shared forms)</label>
                        <div className="flex flex-wrap gap-2">
                          {packs.filter(p => p.id !== uploadPackId).map(p => (
                            <label key={p.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={uploadPackIds.includes(p.id)}
                                onChange={(e) => setUploadPackIds(prev => 
                                  e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id)
                                )}
                              />
                              <span className="text-sm">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!titlesOnly && (
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={uploadRetail} onChange={(e) => setUploadRetail(e.target.checked)} />
                      <span className="text-sm">Use for Retail</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={uploadWholesale} onChange={(e) => setUploadWholesale(e.target.checked)} />
                      <span className="text-sm">Use for Wholesale</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => { setShowUpload(false); setUploadFile(null); setUploadName(''); setUploadPackIds([]); setUploadQueue([]); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || (uploadQueue.length === 0 && (!uploadFile || !uploadName))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : uploadQueue.length > 1 ? `Upload ${uploadQueue.filter(q=>q.status==='pending').length} Forms` : 'Upload & Configure'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blank Form Modal (titlesOnly) */}
        {showBlankForm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm">
              <h3 className="font-semibold text-lg mb-4">Create Blank Form</h3>
              <p className="text-sm text-gray-500 mb-4">A blank white page will be created — open the field editor to add auto-fill fields (including Today's Date).</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Form Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={blankFormName}
                    onChange={(e) => setBlankFormName(e.target.value)}
                    placeholder="e.g., My Custom Title Form"
                    className="w-full border rounded-lg px-3 py-2"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateBlankForm()}
                  />
                </div>
                {packs.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Form Pack</label>
                    <select
                      value={blankFormPackId || ''}
                      onChange={(e) => setBlankFormPackId(parseInt(e.target.value, 10) || null)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      {packs.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => { setShowBlankForm(false); setBlankFormName(''); setBlankFormPackId(null); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBlankForm}
                  disabled={creatingBlank || !blankFormName.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {creatingBlank ? 'Creating...' : 'Create & Edit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link From Pack Modal */}
        {showLinkModal && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-base">Link Forms From Another Pack</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Selected forms will appear in <strong>{packs.find(p => p.id === selectedPackId)?.name}</strong> without re-uploading or re-mapping.
                    Changes to the original form automatically apply here too.
                  </p>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {linkCandidates.length === 0 ? (
                  <p className="text-center text-neutral-400 py-8 text-sm">No other packs have forms yet.</p>
                ) : linkCandidates.map(({ pack, templates }) => (
                  <div key={pack.id}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">📦 {pack.name}</span>
                      <button
                        onClick={() => {
                          const allIds = templates.map(t => t.id);
                          setSelectedLinkIds(prev => {
                            const next = new Set(prev);
                            const allSelected = allIds.every(id => next.has(id));
                            allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                            return next;
                          });
                        }}
                        className="text-xs text-teal-600 hover:underline"
                      >
                        {templates.every(t => selectedLinkIds.has(t.id)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="space-y-1.5 pl-2">
                      {templates.map(t => (
                        <label key={t.id} className="flex items-center gap-3 p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedLinkIds.has(t.id)}
                            onChange={() => {
                              setSelectedLinkIds(prev => {
                                const next = new Set(prev);
                                next.has(t.id) ? next.delete(t.id) : next.add(t.id);
                                return next;
                              });
                            }}
                            className="accent-teal-600"
                          />
                          <span className="text-sm font-medium">{t.name}</span>
                          {t.blank_file_path || t.blank_file_url ? (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-auto">✓ Mapped</span>
                          ) : (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded ml-auto">Needs Setup</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-5 py-3 border-t flex justify-between items-center">
                <span className="text-xs text-neutral-500">{selectedLinkIds.size} form{selectedLinkIds.size !== 1 ? 's' : ''} selected</span>
                <div className="flex gap-2">
                  <button onClick={() => setShowLinkModal(false)} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800">Cancel</button>
                  <button
                    onClick={handleLink}
                    disabled={selectedLinkIds.size === 0 || linking}
                    className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {linking ? 'Linking...' : `Link ${selectedLinkIds.size} Form${selectedLinkIds.size !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* NOTE: the pack sidebar was removed — packs are managed ONLY in the Pack Editor
              (header button) so there is exactly one place that controls grouping/printing. */}

          {/* Templates list */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl text-gray-300 mb-4">📄</div>
                <p className="text-gray-500">No form templates yet</p>
                <p className="text-sm text-gray-400 mt-1">Upload your first form to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    className={`border rounded-lg p-4 ${template.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📄</div>
                        <div>
                          <div className="font-medium">{template.name}</div>
                          <div className="text-sm text-gray-500">
                            {FORM_TYPES.find(t => t.value === template.form_type)?.label || template.form_type}
                          </div>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {template.use_for_retail && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Retail</span>
                            )}
                            {template.use_for_wholesale && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Wholesale</span>
                            )}
                            {template.blank_file_path || template.blank_file_url ? (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded flex items-center gap-1">
                                <span>✓</span> Ready
                              </span>
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex items-center gap-1">
                                <span>⚠</span> Needs Setup
                              </span>
                            )}
                            {template.is_linked && (
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded flex items-center gap-1">
                                ⇠ {template.source_pack_name || 'Linked'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* ONE editor for everything: fields, sign-here markers, white-out.
                            Pack membership is managed in the Pack Editor; deletion (archived
                            forms only) lives in the Form Library. */}
                        <button
                          onClick={() => setFieldEditorTemplate(template)}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          title="Open the Field Editor — place/drag fields, sign-here markers, white-out"
                        >
                          Field Editor
                        </button>
                        {titlesOnly && (
                          <button
                            onClick={async () => {
                              try {
                                // If no fields mapped yet, just open the raw PDF
                                const result = await api.getBlankFillableTemplate(template.id);
                                if (result?.pdfDataUrl) {
                                  const base64 = result.pdfDataUrl.split(',')[1];
                                  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
                                  const blob = new Blob([bytes], { type: 'application/pdf' });
                                  const url = URL.createObjectURL(blob);
                                  const win = window.open(url, '_blank');
                                  if (!win) alert('Allow pop-ups to open the form');
                                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                                } else {
                                  window.open(api.getFormTemplatePdfUrl(template.id), '_blank');
                                }
                              } catch {
                                // Fallback to raw PDF if endpoint fails
                                window.open(api.getFormTemplatePdfUrl(template.id), '_blank');
                              }
                            }}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            title="Open blank fillable form — all fields are typeable, nothing pre-filled"
                          >
                            Open Blank
                          </button>
                        )}
                        <button
                          onClick={() => window.open(api.getFormTemplatePdfUrl(template.id), '_blank')}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="View Original"
                        >
                          <Icon name="eye" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(template)}
                          className={`p-2 rounded ${template.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                          title={template.is_active ? 'Active (click to archive)' : 'Archived (click to restore)'}
                        >
                          <Icon name="check" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visual Field Editor Modal */}
      {fieldEditorTemplate && (
        <FormFieldEditor
          template={fieldEditorTemplate}
          onClose={() => setFieldEditorTemplate(null)}
          onSave={() => {
            loadTemplates();
          }}
        />
      )}

      {/* Pack Editor — compose which forms print together and in what order */}
      {showPackEditor && (
        <PackEditor
          onClose={() => {
            setShowPackEditor(false);
            loadPacks();
            loadTemplates();
          }}
        />
      )}

      {/* Form Library — audit every template, group duplicates, archive/restore */}
      {showFormLibrary && (
        <FormLibrary
          onClose={() => {
            setShowFormLibrary(false);
            loadPacks();
            loadTemplates();
          }}
          onEditFields={(template) => {
            setShowFormLibrary(false);
            setFieldEditorTemplate(template);
          }}
        />
      )}
    </div>
  );
};

export default FormTemplateManager;
